import { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile site key helper. Site key set per ADR-050; locally
 * the env is left empty so signup/login forms render without a captcha
 * widget and Supabase ([auth.captcha].enabled=false) doesn't enforce it.
 */
export function getTurnstileSiteKey(): string | null {
  const key = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  return key && key.length > 0 ? key : null;
}

/**
 * The Turnstile script attaches a `turnstile` global to `window`. This
 * helper is the single point that narrows that `unknown` shape, so
 * call-sites don't repeat the cast.
 */
export interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: { sitekey: string; callback: (token: string) => void },
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

export function getTurnstile(): TurnstileApi | null {
  const w = window as unknown as { turnstile?: TurnstileApi };
  return w.turnstile ?? null;
}

const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";
const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js";
const TURNSTILE_POLL_MS = 100;
// Long enough to absorb a slow 3G/4G load and Cloudflare cold-start;
// short enough that a CSP-blocked or ad-blocked load surfaces as a
// visible error instead of silently leaving submit buttons disabled.
const TURNSTILE_TIMEOUT_MS = 10_000;

function ensureTurnstileScript(): void {
  if (document.getElementById(TURNSTILE_SCRIPT_ID)) return;
  const script = document.createElement("script");
  script.id = TURNSTILE_SCRIPT_ID;
  script.src = TURNSTILE_SCRIPT_URL;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

export interface UseTurnstileResult {
  /** Site key in use, or null when Turnstile is unconfigured. */
  siteKey: string | null;
  /** Most recent token from a completed challenge. Single-use — clear via `reset()` after consuming. */
  captchaToken: string | null;
  /** Attach to the `<div>` that should host the widget. */
  // React 19's `useRef<T>(null)` returns `RefObject<T | null>`. Match
  // that exactly so callers don't get a "not assignable" surprise.
  ref: React.RefObject<HTMLDivElement | null>;
  /** Discard the current token and re-render the widget for a fresh challenge. */
  reset: () => void;
  /** True when Turnstile is unconfigured — callers should skip captcha gating entirely. */
  skipped: boolean;
  /**
   * True when the Turnstile script failed to load within
   * TURNSTILE_TIMEOUT_MS, or when render() threw. Callers should show
   * a visible error so users know why their submit button is disabled.
   * Resets to `false` whenever `phase` changes (giving the next mount
   * a fresh attempt).
   */
  failed: boolean;
}

/**
 * Mount Cloudflare Turnstile into a ref'd div and surface the
 * resolved token + a reset hook. Designed so callers don't need to
 * touch the global `window.turnstile` API directly.
 *
 * The optional `phase` key forces a re-mount when it changes — used by
 * the signup form, which moves the widget from the form view to the
 * "Check your inbox" view after the first submit. Most callers can
 * omit it.
 *
 * Lifecycle invariants:
 * - widgetId is held in a ref because it never drives render — using
 *   state would queue an extra paint on every successful mount.
 * - The cleanup callback calls `turnstile.remove(widgetId)` on
 *   unmount and on phase change, so the iframe + listeners don't leak
 *   when the host component goes away mid-challenge.
 * - The token callback is guarded against firing after unmount; the
 *   Cloudflare runtime can resolve the challenge after our cleanup
 *   runs and we don't want a setState-after-unmount warning.
 * - The poll terminates one of three ways: (a) `window.turnstile`
 *   populates and we mount; (b) `TURNSTILE_TIMEOUT_MS` elapses with
 *   no script load and `failed` flips to `true` so the caller can
 *   render a visible error; (c) the host component unmounts.
 */
export function useTurnstile(phase?: string): UseTurnstileResult {
  const siteKey = getTurnstileSiteKey();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const widgetIdRef = useRef<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const mountedForRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!siteKey) return;
    const currentPhase = phase ?? "default";
    if (mountedForRef.current === currentPhase) return;
    // Phase transition resets any prior failure so the next attempt
    // can succeed (e.g. user fixed network and resubmitted).
    setFailed(false);
    ensureTurnstileScript();
    const startedAt = Date.now();
    const interval = setInterval(() => {
      if (Date.now() - startedAt > TURNSTILE_TIMEOUT_MS) {
        // Script never populated `window.turnstile` — most likely
        // cause is CSP block, ad blocker, or offline. Surface a
        // visible failure so the form stops looking idle.
        console.warn(
          `[useTurnstile] script did not load within ${String(TURNSTILE_TIMEOUT_MS)}ms`,
        );
        if (isMountedRef.current) setFailed(true);
        clearInterval(interval);
        return;
      }
      const turnstile = getTurnstile();
      if (turnstile && ref.current && mountedForRef.current !== currentPhase) {
        mountedForRef.current = currentPhase;
        try {
          widgetIdRef.current = turnstile.render(ref.current, {
            sitekey: siteKey,
            callback: (token: string) => {
              if (isMountedRef.current) setCaptchaToken(token);
            },
          });
        } catch (err) {
          // Most likely cause: invalid sitekey or the host div is
          // already in use by another widget. Logging beats a silent
          // dead poll — the form's submit button stays disabled and
          // there is otherwise no signal something failed.
          console.error("[useTurnstile] render failed:", err);
          mountedForRef.current = null;
          if (isMountedRef.current) setFailed(true);
        }
        clearInterval(interval);
      }
    }, TURNSTILE_POLL_MS);
    return () => {
      clearInterval(interval);
      const widgetId = widgetIdRef.current;
      if (widgetId !== null) {
        getTurnstile()?.remove(widgetId);
        widgetIdRef.current = null;
        mountedForRef.current = null;
      }
    };
  }, [siteKey, phase]);

  function reset() {
    if (!siteKey) return;
    setCaptchaToken(null);
    const widgetId = widgetIdRef.current;
    if (widgetId === null) return;
    getTurnstile()?.reset(widgetId);
  }

  return {
    siteKey,
    captchaToken,
    ref,
    reset,
    skipped: !siteKey,
    failed,
  };
}
